// src/components/shared/modals/RequirementChangeModal.tsx
//
// UMD-only modal. The REQUIREMENT_OPTIONS below hardcode UMD's CS
// curriculum (CMSC131/132/216/250, CMSC4XX regex, MATH/STAT regex, etc.).
// SJSU users use SJSU-format codes (CS 46A, MATH 30) which never match
// any validator here — opening this modal would show "no eligible
// requirements" with confusing options. Per Phase G, the modal is
// gated to UMD via useInstitutionConfig().slug; non-UMD users see no
// content. Generalizing this UI to be data-driven from the backend
// (per-institution requirement classifications) is a separate
// post-pilot workstream.
import { useState, useEffect } from 'react';
import { X, AlertCircle, Check } from 'lucide-react';
import { ModalShell } from '../../ui';
import { useInstitutionConfig } from '../../../contexts/InstitutionConfigContext';

interface CourseLike {
  code?: string;
  title?: string;
  course_title?: string;
  gen_ed_requirements?: string[] | null;
}

interface RequirementOption {
  id: string;
  name: string;
  description: string;
  validator: (course: CourseLike) => boolean;
}

const REQUIREMENT_OPTIONS: RequirementOption[] = [
  {
    id: 'core-cs',
    name: 'Core CS Courses',
    description: 'Foundation courses required for all CS majors',
    validator: (course) => {
      const CORE_CS_COURSES = ['CMSC131', 'CMSC132', 'CMSC216', 'CMSC250', 'CMSC330', 'CMSC351'];
      return !!course.code && CORE_CS_COURSES.includes(course.code);
    },
  },
  {
    id: 'upper-level-cs',
    name: 'Upper Level CS Electives',
    description: '400-level computer science courses',
    validator: (course) => !!course.code && /^CMSC4\d{2}$/.test(course.code),
  },
  {
    id: 'cs-area-systems',
    name: 'CS Area - Systems',
    description: 'Computer systems, networks, and security',
    validator: (course) => {
      const SYSTEMS_COURSES = ['CMSC411', 'CMSC412', 'CMSC414', 'CMSC416', 'CMSC417'];
      return !!course.code && SYSTEMS_COURSES.includes(course.code);
    },
  },
  {
    id: 'cs-area-info-proc',
    name: 'CS Area - Information Processing',
    description: 'AI, databases, and data processing',
    validator: (course) => {
      const INFO_PROC_COURSES = [
        'CMSC420',
        'CMSC421',
        'CMSC422',
        'CMSC423',
        'CMSC424',
        'CMSC426',
        'CMSC427',
      ];
      return !!course.code && INFO_PROC_COURSES.includes(course.code);
    },
  },
  {
    id: 'cs-area-software',
    name: 'CS Area - Software Engineering',
    description: 'Software design and development',
    validator: (course) => {
      const SOFTWARE_COURSES = ['CMSC430', 'CMSC433', 'CMSC434', 'CMSC435', 'CMSC436'];
      return !!course.code && SOFTWARE_COURSES.includes(course.code);
    },
  },
  {
    id: 'cs-area-theory',
    name: 'CS Area - Theory',
    description: 'Algorithms and computational theory',
    validator: (course) => {
      const THEORY_COURSES = ['CMSC451', 'CMSC452', 'CMSC454', 'CMSC456', 'CMSC457'];
      return !!course.code && THEORY_COURSES.includes(course.code);
    },
  },
  {
    id: 'cs-area-numerical',
    name: 'CS Area - Numerical Analysis',
    description: 'Computational mathematics',
    validator: (course) => {
      const NUMERICAL_COURSES = ['CMSC460', 'CMSC466', 'CMSC470', 'CMSC474'];
      return !!course.code && NUMERICAL_COURSES.includes(course.code);
    },
  },
  {
    id: 'math',
    name: 'Mathematics Requirements',
    description: 'Required mathematics courses',
    validator: (course) =>
      !!course.code && (/^MATH\d{3}$/.test(course.code) || /^STAT\d{3}$/.test(course.code)),
  },
  {
    id: 'gen-ed',
    name: 'General Education',
    description: 'Liberal arts and sciences requirements',
    validator: (course) =>
      !!course.gen_ed_requirements && course.gen_ed_requirements.length > 0,
  },
];

export interface RequirementChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  course: CourseLike | null | undefined;
  // Renamed from `currentRequirements` historically — kept as `currentRequirement` to match callers.
  currentRequirement?: string[] | unknown[] | null;
  // Pass-through prop kept for compatibility with callers; not used internally.
  requirements?: unknown;
  onSave: (course: CourseLike, requirements: string[]) => void;
}

const RequirementChangeModal = ({
  isOpen,
  onClose,
  course,
  currentRequirement = [],
  requirements: _requirements,
  onSave,
}: RequirementChangeModalProps) => {
  // Phase G: gate to UMD. REQUIREMENT_OPTIONS hardcodes UMD's CS
  // curriculum; SJSU's catalog uses different course codes that never
  // match any validator. Until per-institution requirement data is
  // available from the backend, the safe behavior is to hide the modal
  // for non-UMD users.
  const { config } = useInstitutionConfig();
  const isUmd = config.slug === 'umd';

  const currentRequirementsArray: string[] = Array.isArray(currentRequirement)
    ? (currentRequirement as string[])
    : [];

  const [selectedRequirements, setSelectedRequirements] = useState<Set<string>>(
    new Set(currentRequirementsArray)
  );
  const [warnings, setWarnings] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen && currentRequirement) {
      const reqArray = Array.isArray(currentRequirement) ? (currentRequirement as string[]) : [];
      setSelectedRequirements(new Set(reqArray));
    }
  }, [isOpen, currentRequirement]);

  const handleToggleRequirement = (requirementId: string) => {
    const newSelected = new Set(selectedRequirements);
    if (newSelected.has(requirementId)) {
      newSelected.delete(requirementId);
    } else {
      newSelected.add(requirementId);
    }
    setSelectedRequirements(newSelected);
    checkWarnings(newSelected);
  };

  const checkWarnings = (selected: Set<string>) => {
    const newWarnings: string[] = [];
    currentRequirementsArray.forEach((reqId) => {
      if (!selected.has(reqId)) {
        const requirement = REQUIREMENT_OPTIONS.find((r) => r.id === reqId);
        if (requirement) {
          newWarnings.push(
            `Removing from "${requirement.name}" may leave this requirement unfulfilled`
          );
        }
      }
    });
    setWarnings(newWarnings);
  };

  const handleSave = () => {
    if (!course) return;
    onSave(course, Array.from(selectedRequirements));
    onClose();
  };

  if (!isOpen || !course) return null;
  // Phase G: non-UMD institutions don't yet have a requirement classifier;
  // return null rather than render a modal whose options never match.
  if (!isUmd) return null;

  const eligibleRequirements = REQUIREMENT_OPTIONS.filter((req) => req.validator(course));

  return (
    <ModalShell
      open={isOpen}
      onClose={onClose}
      ariaLabel="Change requirements"
      maxWidth="max-w-2xl"
      className="max-h-[90vh] overflow-hidden"
    >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Change Requirements</h2>
            <p className="text-sm text-gray-600 mt-1">
              {course.code} - {course.title || course.course_title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-sunken rounded-control transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-12rem)]">
          {currentRequirementsArray.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Currently Fulfilling</h3>
              <div className="flex flex-wrap gap-2">
                {currentRequirementsArray.map((reqId) => {
                  const req = REQUIREMENT_OPTIONS.find((r) => r.id === reqId);
                  return req ? (
                    <span
                      key={reqId}
                      className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm"
                    >
                      {req.name}
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          )}

          {warnings.length > 0 && (
            <div className="mb-6 bg-status-warning/10 border border-status-warning/25 rounded-card p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-900 mb-1">Warning</h4>
                  <ul className="text-sm text-amber-700 space-y-1">
                    {warnings.map((warning, idx) => (
                      <li key={idx}>• {warning}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Select Requirements</h3>
            <div className="space-y-2">
              {eligibleRequirements.map((requirement) => {
                const isSelected = selectedRequirements.has(requirement.id);
                const isCurrentlyAssigned = currentRequirementsArray.includes(requirement.id);

                return (
                  <label
                    key={requirement.id}
                    className={`block p-4 border rounded-lg cursor-pointer transition-all ${
                      isSelected
                        ? 'border-primary bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleRequirement(requirement.id)}
                        className="mt-1 text-primary rounded focus:ring-primary"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{requirement.name}</span>
                          {isCurrentlyAssigned && (
                            <span className="text-xs bg-surface-sunken text-ink-2 px-2 py-0.5 rounded-full">
                              Current
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{requirement.description}</p>
                      </div>
                      {isSelected && <Check className="h-5 w-5 text-primary flex-shrink-0" />}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {eligibleRequirements.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No eligible requirements</p>
              <p className="text-sm mt-1">This course doesn't match any degree requirements</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <p className="text-xs text-gray-500">
            {selectedRequirements.size} requirement{selectedRequirements.size !== 1 ? 's' : ''}{' '}
            selected
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={eligibleRequirements.length === 0}
              className="px-4 py-2 text-white bg-primary rounded-md hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Save Changes
            </button>
          </div>
        </div>
    </ModalShell>
  );
};

export default RequirementChangeModal;
